import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { useLanguage } from '../../contexts/LanguageContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BANNER_KEY = 'edotask_clouds_banner';

const PROVIDERS = [
  {
    key: 'google',
    label: 'Google Drive',
    accent: 'from-blue-500 to-emerald-500',
    icon: (
      <svg viewBox="0 0 87 76" className="w-6 h-6" aria-hidden="true">
        <path fill="#0F9D58" d="M43.65 0L21.83 37.98h43.65z"/>
        <path fill="#F4B400" d="M14.56 56.9l10.9-18.92L3.64 0z"/>
        <path fill="#4285F4" d="M65.48 37.98l10.92 18.92L87.3 37.98z"/>
        <path fill="#DB4437" d="M14.56 56.9h58.17l-10.9-18.92H25.47z"/>
      </svg>
    )
  }
];

export default function ConnectedCloudsPage({ onBack }) {
  const { t, tWithFallback } = useLanguage();
  const [connections, setConnections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionProvider, setActionProvider] = useState(null);
  const [banner, setBannerState] = useState(() => {
    try { return JSON.parse(sessionStorage.getItem(BANNER_KEY)); } catch { return null; }
  });
  // Persist so a remount (parent re-render) right after the OAuth redirect keeps the message.
  const setBanner = useCallback((b) => {
    if (b) sessionStorage.setItem(BANNER_KEY, JSON.stringify(b));
    else sessionStorage.removeItem(BANNER_KEY);
    setBannerState(b);
  }, []);

  useEffect(() => {
    if (!banner) return undefined;
    const timer = setTimeout(() => sessionStorage.removeItem(BANNER_KEY), 8000);
    return () => clearTimeout(timer);
  }, [banner]);

  const token = () => localStorage.getItem('token');
  const authHeaders = () => ({ Authorization: `Bearer ${token()}` });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/cloud/connections`, { headers: authHeaders() });
      setConnections(res.data?.connections || []);
    } catch (e) {
      console.error('[clouds] load failed', e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    // Read ?connected=1&provider=... banner left by OAuth callback.
    const params = new URLSearchParams(window.location.search);
    if (params.get('connected') === '1') {
      const p = params.get('provider');
      setBanner({ kind: 'success', text: tWithFallback('cloudConnectedBanner', 'Connected successfully.') + (p ? ` (${p === 'google' ? 'Google Drive' : 'OneDrive'})` : '') });
      window.history.replaceState({}, '', window.location.pathname);
    } else if (params.get('error') === 'drive_permission_missing') {
      setBanner({ kind: 'error', text: tWithFallback('cloudDrivePermissionMissing', 'Google Drive was not connected because the Drive permission was unticked. Please connect again and tick the Google Drive box.') });
    } else if (params.get('error')) {
      setBanner({ kind: 'error', text: tWithFallback('cloudConnectFailed', 'Connection failed. Please try again.') + ` (${params.get('error')})` });
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, [load, tWithFallback, setBanner]);

  const connect = async (provider) => {
    try {
      setActionProvider(provider);
      const res = await axios.get(`${API}/cloud/${provider}/connect`, { headers: authHeaders() });
      if (res.data?.authorization_url) {
        window.location.href = res.data.authorization_url;
      }
    } catch (e) {
      console.error('[clouds] connect failed', e);
      setBanner({ kind: 'error', text: e?.response?.data?.detail || tWithFallback('cloudConnectFailed', 'Connection failed.') });
      setActionProvider(null);
    }
  };

  const disconnect = async (provider) => {
    if (!window.confirm(tWithFallback('cloudDisconnectConfirm', 'Disconnect this cloud? Existing attachments will keep working, but new uploads will not be possible until you reconnect.'))) return;
    try {
      setActionProvider(provider);
      await axios.delete(`${API}/cloud/${provider}`, { headers: authHeaders() });
      await load();
      setBanner({ kind: 'success', text: tWithFallback('cloudDisconnected', 'Cloud disconnected.') });
    } catch (e) {
      console.error('[clouds] disconnect failed', e);
      setBanner({ kind: 'error', text: tWithFallback('cloudDisconnectFailed', 'Could not disconnect.') });
    } finally {
      setActionProvider(null);
    }
  };

  const connByProvider = Object.fromEntries(connections.map(c => [c.provider, c]));

  return (
    <div className="max-w-2xl mx-auto p-4" data-testid="connected-clouds-page">
      {onBack && (
        <button
          onClick={onBack}
          data-testid="clouds-back-btn"
          className="text-sm text-gray-600 hover:text-gray-900 mb-4 inline-flex items-center gap-1"
        >
          ← {t('back') || 'Back'}
        </button>
      )}

      <div className="mb-6">
        <h2 className="font-display text-2xl font-bold text-gray-900 mb-1">
          {tWithFallback('connectedCloudsTitle', 'Connected Clouds')}
        </h2>
        <p className="text-sm text-gray-600">
          {tWithFallback('connectedCloudsSubtitle', 'Your task files are stored in your own cloud. We only keep a reference — never the file itself.')}
        </p>
      </div>

      {banner && (
        <div
          data-testid="clouds-banner"
          className={`rounded-lg px-4 py-3 mb-5 text-sm ${banner.kind === 'success' ? 'bg-green-50 text-green-800 border border-green-200' : 'bg-red-50 text-red-800 border border-red-200'}`}
        >
          {banner.text}
        </div>
      )}

      <div className="space-y-3">
        {PROVIDERS.map((p) => {
          const conn = connByProvider[p.key];
          const connected = Boolean(conn);
          const busy = actionProvider === p.key;
          return (
            <div
              key={p.key}
              data-testid={`cloud-card-${p.key}`}
              className="bg-white rounded-xl border border-gray-200 p-4 flex items-start gap-4 shadow-sm"
            >
              <div className={`shrink-0 w-11 h-11 rounded-lg bg-gradient-to-br ${p.accent} flex items-center justify-center text-white`}>
                {p.icon}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <div className="font-semibold text-gray-900">{p.label}</div>
                    {connected ? (
                      <div className="text-xs text-gray-500 truncate" data-testid={`cloud-account-${p.key}`}>
                        {conn.account_email || conn.account_name || tWithFallback('cloudConnectedShort', 'Connected')}
                      </div>
                    ) : (
                      <div className="text-xs text-gray-500">
                        {tWithFallback('cloudNotConnected', 'Not connected')}
                      </div>
                    )}
                  </div>
                  {connected ? (
                    <button
                      onClick={() => disconnect(p.key)}
                      disabled={busy || loading}
                      data-testid={`cloud-disconnect-${p.key}`}
                      className="shrink-0 text-sm font-medium px-3 py-1.5 rounded-lg border border-red-200 text-red-700 hover:bg-red-50 disabled:opacity-60"
                    >
                      {busy ? '…' : tWithFallback('disconnectBtn', 'Disconnect')}
                    </button>
                  ) : (
                    <button
                      onClick={() => connect(p.key)}
                      disabled={busy || loading}
                      data-testid={`cloud-connect-${p.key}`}
                      className="shrink-0 text-sm font-medium px-3 py-1.5 rounded-lg bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-60"
                    >
                      {busy ? '…' : tWithFallback('connectBtn', 'Connect')}
                    </button>
                  )}
                </div>
                {conn?.reauth_required && (
                  <div className="mt-2 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1" data-testid={`cloud-reauth-${p.key}`}>
                    {tWithFallback('cloudReauthRequired', 'Access expired — please reconnect to keep attaching files.')}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      <div className="mt-6 rounded-lg bg-blue-50 border border-blue-200 px-4 py-3 text-xs text-blue-900">
        <strong>{tWithFallback('privacyLabel', 'Privacy')}:</strong>{' '}
        {tWithFallback(
          'cloudsPrivacyNote',
          'We request the minimum permission needed: Google Drive\'s drive.file, so we can only see files this app creates. Teammates on a team task get view-only access. Your Drive tokens are encrypted at rest.'
        )}
      </div>
    </div>
  );
}
